import { motion, type HTMLMotionProps } from "motion/react";
import { Loader2 } from "lucide-react";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { press } from "@/lib/motion";

type Variant = "primary" | "secondary" | "ghost" | "danger";

const styles: Record<Variant, string> = {
  primary: "bg-accent text-accent-contrast shadow-[0_6px_20px_-6px_var(--accent)]",
  secondary: "bg-accent-soft text-accent",
  ghost: "text-accent",
  danger: "bg-negative/12 text-negative",
};

interface Props extends HTMLMotionProps<"button"> {
  variant?: Variant;
  loading?: boolean;
  block?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", loading, block, className, children, disabled, ...rest },
  ref,
) {
  return (
    <motion.button
      ref={ref}
      whileTap={disabled || loading ? undefined : press}
      disabled={disabled || loading}
      className={cn(
        "inline-flex min-h-11 items-center justify-center gap-2 rounded-[12px] px-5 text-headline transition-opacity disabled:opacity-50",
        styles[variant],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </motion.button>
  );
});

export function IconButton({
  label,
  children,
  className,
  ...rest
}: HTMLMotionProps<"button"> & { label: string; children: ReactNode }) {
  return (
    <motion.button
      type="button"
      whileTap={press}
      aria-label={label}
      title={label}
      className={cn(
        "grid size-11 place-items-center rounded-full text-accent transition-colors hover:bg-accent-soft",
        className,
      )}
      {...rest}
    >
      {children}
    </motion.button>
  );
}
